import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import { consolidateCitations, escapeCitationMarkers } from '@/lib/citations'
import { remarkPlugins } from '@/lib/markdown'

export type MarkdownProps = {
  /** Raw LLM-authored text. May be a partial chunk during streaming. */
  children: string
}

/**
 * Renders LLM-authored bot answers as markdown (react-markdown + remark-gfm),
 * with no `dangerouslySetInnerHTML` — react-markdown emits React elements, so
 * the LLM output is never injected as raw HTML.
 *
 * Citation markers are consolidated (`[1][2]` → `[1, 2]`) and then escaped so
 * they survive the markdown pass as literal text rather than being parsed as
 * links. Both transforms are pure lib calls; styling is supplied by the
 * wrapping component's CSS Module via descendant selectors.
 *
 * Safe to feed partial markdown during streaming — react-markdown renders an
 * unclosed `**`/list/heading as literal text until the closing token arrives,
 * so progressive chunks never crash or flicker structurally.
 */
export default function Markdown({ children }: MarkdownProps) {
  const prepared = useMemo(() => escapeCitationMarkers(consolidateCitations(children)), [children])
  return <ReactMarkdown remarkPlugins={remarkPlugins}>{prepared}</ReactMarkdown>
}
